import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { Accordion } from "react-bootstrap";
import { ReactComponent as ChevronDownIcon } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "assets/images/iconsV2/chevron-up.svg";
import { FilterConfig } from "types/Common";
import Badge from "components/v2/Badge/Badge";
import FreightModeFilter from "components/organisms/Filter/components/FreightModeFilter";
import AccordionFilter from "components/organisms/Filter/components/AccordionFilter";
import MultiSelectFilter from "./MultiSelectFilter";
import SearchableTextFilter from "./SearchableTextFilter";
import TextFilter from "./TextFilter";
import ButtonGroupFilter from "./ButtonGroupFilter";
import TabsFilter from "./TabsFilter";
import DateFilterV2 from "./DateFilter";

interface FilterAccordionsProps {
  config: FilterConfig[];
  pendingFilters: Record<string, any>;
  setPendingFilters: Dispatch<SetStateAction<Record<string, any> | undefined>> | Function;
  eventContext: string;
}
function FilterAccordions({ config, pendingFilters, setPendingFilters, eventContext }: FilterAccordionsProps) {
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(undefined);
  const isFilterSelected = (filterValue: string | object | Array<any> | undefined) => {
    if ((Array.isArray(filterValue) && filterValue.length) || (!Array.isArray(filterValue) && filterValue)) {
      return true;
    }
    return false;
  };
  const renderFilterComponent = useCallback(
    (filter: FilterConfig) => {
      switch (filter.filterType) {
        case "multiselect":
          return (
            <MultiSelectFilter
              currentFilters={pendingFilters}
              setFilters={setPendingFilters}
              options={filter.options}
              filterKey={filter.key}
              searchable={filter.isSearchable}
              eventContext={eventContext}
            />
          );
        case "buttongroup":
          return (
            <ButtonGroupFilter
              currentFilters={pendingFilters}
              setFilters={setPendingFilters}
              filterKey={filter.key}
              options={filter.options}
            />
          );
        case "tabfilter":
          if (filter.buttonGroupOptions && filter.tabType) {
            return (
              // @ts-ignore
              <TabsFilter
                buttonGroupOptions={filter.buttonGroupOptions}
                setFilters={setPendingFilters}
                currentFilters={pendingFilters}
                type={filter.tabType}
                {...(filter.options && { options: filter.options })}
                {...(filter.isSearchable !== undefined && { isSearchable: filter.isSearchable })}
                {...(eventContext && { eventContext })}
              />
            );
          }
          break;
        case "freight_mode":
          return (
            <FreightModeFilter
              currentFilters={pendingFilters}
              setFilters={setPendingFilters}
              filterKey={filter.key}
              eventContext={eventContext}
              filterValueKey={filter.filterValueKey}
            />
          );
        case "date":
          return (
            <div>
              <DateFilterV2
                currentFilters={pendingFilters}
                setFilters={setPendingFilters}
                filterKey={filter.key}
                eventContext={eventContext}
              />
            </div>
          );
        case "text":
          return filter.options ? (
            <SearchableTextFilter
              currentFilters={pendingFilters}
              setFilters={setPendingFilters}
              filterKey={filter.key}
              options={filter.options}
            />
          ) : (
            <TextFilter currentFilters={pendingFilters} setFilters={setPendingFilters} filterKey={filter.key} />
          );
        case "accordion":
          return (
            <AccordionFilter
              renderFilterComponent={renderFilterComponent}
              nestedFilters={filter.nestedFilters}
              isFilterSelected={isFilterSelected}
              currentFilters={pendingFilters}
            />
          );
        default:
          return null;
      }
    },
    [pendingFilters],
  );
  return (
    <Accordion onSelect={(eventKey) => setActiveAccordion(eventKey as string)} activeKey={activeAccordion}>
      {config.map((filter) => (
        <div key={filter.key} className="filter-accordion-container">
          <Accordion.Toggle as="div" eventKey={filter.key}>
            <div className="d-flex align-items-center justify-content-between cursor-pointer">
              {/** Label for the accordion */}
              <p className="filter-accordion-label text-md font-medium">{filter.label}</p>
              <div className="d-flex align-items-center">
                {/** Badge for selected filters count */}
                {filter.filterType === "tabfilter" && filter.buttonGroupOptions
                  ? filter.buttonGroupOptions.some((option) => isFilterSelected(pendingFilters[option.value])) && (
                      <div className="mr-xl">
                        <Badge
                          color="gray"
                          type="category-badge"
                          label={filter.buttonGroupOptions.reduce((total, option) => {
                            const selectedOptions = pendingFilters[option.value];
                            return total + (Array.isArray(selectedOptions) ? selectedOptions.length : 0);
                          }, 0)}
                        />
                      </div>
                    )
                  : isFilterSelected(pendingFilters[filter.key]) && (
                      <div className="mr-xl">
                        <Badge
                          color="gray"
                          type="category-badge"
                          label={Array.isArray(pendingFilters[filter.key]) ? pendingFilters[filter.key]?.length : 1}
                        />
                      </div>
                    )}
                {/** Icon for expanded/collapsed accordion */}
                {activeAccordion === filter.key ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </div>
          </Accordion.Toggle>
          {/** Render the filter component if the accordion is expanded */}
          <Accordion.Collapse eventKey={filter.key}>
            <div className="mt-3">{renderFilterComponent(filter)}</div>
          </Accordion.Collapse>
        </div>
      ))}
    </Accordion>
  );
}
export default FilterAccordions;
